import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { shouldHideError } from '@services/helpers/server-error-silence';
import { HELP_DEFAULT_REPORT_MODULE_KEY } from '../constants/help-module.constants';
import { HelpWalkthrough } from '../models/help-api.types';
import { HELP_LEAD_DETAIL_GETTING_STARTED, HelpGettingStartedItem } from '../models/help-leads-getting-started';
import { HelpLeadsPublicApiService } from '../services/help-leads-public-api.service';
import { HelpTargetRegistryService } from '../services/help-target-registry.service';
import { HelpWalkthroughRunnerService } from '../services/help-walkthrough-runner.service';
import { isHelpTargetElementVisible } from '../utils/help-target-visibility';
import { resolveHelpTargetElement } from '../utils/resolve-help-target-element';

@Component({
  selector: 'app-help-drawer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './help-drawer.component.html',
  styleUrls: ['./help-drawer.component.scss'],
})
export class HelpDrawerComponent implements OnInit, OnChanges {
  @Input() open = false;
  @Input() moduleKey = HELP_DEFAULT_REPORT_MODULE_KEY;
  @Output() openChange = new EventEmitter<boolean>();
  @Output() tourStarted = new EventEmitter<void>();

  searchQuery = '';
  walkthroughs: HelpWalkthrough[] = [];
  /** Count from API before DOM filtering — used for empty-list copy. */
  publishedWalkthroughCount = 0;
  loading = false;
  loadError: string | null = null;
  gettingStarted: HelpGettingStartedItem[] = HELP_LEAD_DETAIL_GETTING_STARTED;
  checklist: Record<string, boolean> = {};

  constructor(
    private readonly helpApi: HelpLeadsPublicApiService,
    private readonly runner: HelpWalkthroughRunnerService,
    private readonly targetRegistry: HelpTargetRegistryService
  ) {}

  ngOnInit(): void {
    for (const item of this.gettingStarted) {
      this.checklist[item.id] = false;
    }
  }

  /**
   * Parent drives `[(open)]` via `helpDrawerOpen = true` — that updates `@Input` only and does not run
   * `openChange`. Load published help guides whenever `open` becomes true.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['moduleKey'] && this.open && !changes['moduleKey'].firstChange) {
      this.refresh();
      return;
    }
    if (changes['open'] && this.open) {
      this.refresh();
    }
  }

  onOpenChange(next: boolean): void {
    this.open = next;
    this.openChange.emit(next);
  }

  close(): void {
    this.onOpenChange(false);
  }

  refresh(): void {
    this.loading = true;
    this.loadError = null;
    this.helpApi.getPublishedWalkthroughs(this.moduleKey).subscribe({
      next: list => {
        const published = list ?? [];
        this.publishedWalkthroughCount = published.length;
        // Defer past CD / layout so [helpTarget] registers and layout settles before we check the DOM.
        this.scheduleApplyWalkthroughFilter(published);
      },
      error: err => {
        if (!shouldHideError(err)) {
          this.loadError = err?.message ?? 'Unable to load help guides.';
        }
        this.walkthroughs = [];
        this.publishedWalkthroughCount = 0;
        this.loading = false;
      },
    });
  }

  startTour(w: HelpWalkthrough): void {
    if (!w?.steps?.length) {
      return;
    }
    this.runner.start(w.name, w.steps);
    this.tourStarted.emit();
    this.close();
  }

  toggleChecklist(id: string): void {
    this.checklist[id] = !this.checklist[id];
  }

  get tourListEmptyMessage(): string {
    if (this.publishedWalkthroughCount === 0) {
      return 'No published help guides yet.';
    }
    return 'No help guides available for this screen.';
  }

  private scheduleApplyWalkthroughFilter(published: HelpWalkthrough[]): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.walkthroughs = this.filterWalkthroughsWithVisibleFirstStep(published);
          this.loading = false;
        }, 0);
      });
    });
  }

  /**
   * Only list help guides whose first step anchors to an element on this screen (registered via {@link HelpTargetDirective}),
   * is visible to the user (not CSS-hidden), and has layout size. Guides for other pages or hidden panels are omitted.
   */
  private filterWalkthroughsWithVisibleFirstStep(list: HelpWalkthrough[]): HelpWalkthrough[] {
    return list.filter(w => this.isFirstStepTargetVisible(w));
  }

  private isFirstStepTargetVisible(w: HelpWalkthrough): boolean {
    const steps = [...(w.steps ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const first = steps[0];
    if (!first?.target_key) {
      return false;
    }
    return this.isTargetElementPresent(first.target_key);
  }

  private isTargetElementPresent(targetKey: string): boolean {
    const el = resolveHelpTargetElement(this.targetRegistry, targetKey);
    if (!el) {
      return false;
    }
    return isHelpTargetElementVisible(el);
  }
}
